import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Formik } from 'formik'

import AdvancedSetupSection from '../components/AdvancedSetupSection'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import MediaUpload from '../../MediaUpload/MediaUpload'
import EditorText from '../../EditorText/EditorText'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { DEFAULT_TEMPLATE, DEFAULT_TEMPLATE_FIELDS, getTemplateData } from '../helpers'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import API from '../../../api/api'

import makeValidationSchema from './validation'
import Navbar from './components/Navbar'

const InspectionTemplateView = ({ building }) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [isLoading, setIsLoading] = useState(false)
  const [template, setTemplate] = useState(null)
  const [isWarningPopupOpen, setIsWarningPopupOpen] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const { templateId } = useParams()

  const onBack = () =>
    navigate('/dashboard/building-dashboard/internal-inspections/setup/templates')

  const getInitialValues = () => {
    if (template) {
      const initial = DEFAULT_TEMPLATE_FIELDS.reduce((acc, key) => {
        acc[key] = template[key]
        return acc
      }, {})
      initial.advanced = !!(initial.rooms && initial.rooms.length)
      return initial
    }
    return DEFAULT_TEMPLATE
  }

  const updateParams = () => {
    const path = '/dashboard/building-dashboard/internal-inspections/template/edit'
    navigate(`${path}/${template.id}`, { replace: true })
  }

  const checkLocation = () => {
    if (location.pathname.includes('/template/create')) {
      updateParams()
    } else if (location.pathname.includes('/template/view')) {
      setIsPreview(true)
    }
  }

  const fetchTemplateById = async (id) => {
    try {
      setIsLoading(true)
      const { data } = await API.getInspectionById(id)
      setTemplate(data)
    } catch {
      displaySnackbar(t('internal-inspections:templateView.errorFetching'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdate = async (values) => {
    setIsLoading(true)
    const requestData = getTemplateData(values)

    try {
      const { data } = await API.updateInspectionTemplate(template.id, requestData)
      setTemplate(data)
      displaySnackbar(t('internal-inspections:templateView.updatedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:templateView.errorUpdating'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async (values) => {
    setIsLoading(true)

    const requestData = getTemplateData(values)

    try {
      await API.createInspectionTemplate({
        ...requestData,
        buildingId: building.id,
      })
      displaySnackbar(t('internal-inspections:templateView.createdSuccessfully'))
      navigate(-1)
    } catch {
      displaySnackbar(t('internal-inspections:templateView.errorCreating'), true)
    }
  }

  useEffect(() => {
    if (template) {
      checkLocation()
    }
  }, [template])

  useEffect(() => {
    if (building && templateId && !template) {
      fetchTemplateById(templateId)
    }
  }, [building, template])

  return (
    <React.Fragment>
      <Formik
        validationSchema={makeValidationSchema(t)}
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={template ? handleUpdate : handleSave}
      >
        {({
          values,
          isValid,
          isSubmitting,
          handleChange,
          handleSubmit,
          setFieldValue,
          dirty,
          errors,
        }) => (
          <React.Fragment>
            <Navbar
              onBack={() => (dirty ? setIsWarningPopupOpen(true) : onBack())}
              onSubmit={handleSubmit}
              disabled={
                !isValid ||
                isSubmitting ||
                !values.name ||
                !values.templateName ||
                !dirty ||
                isPreview
              }
              isEdit={!!templateId}
              isPreview={isPreview}
            />
            <div
              className={`ctp-internal-inspections__view horizontal ${
                isLoading ? 'is-loading' : ''
              }${isPreview ? 'disableable-container' : ''}`}
            >
              <div className="ctp-internal-inspections__view__content">
                <span className="inspections-title">
                  {t('internal-inspections:templateView.templateSettings')}
                </span>
                <div className="row-settings">
                  <div>
                    <span className="input-label">
                      {t('internal-inspections:templateView.templateName')}
                    </span>
                    <TextField
                      name="templateName"
                      onChange={handleChange}
                      value={values.templateName}
                      placeholder={t(
                        'internal-inspections:templateView.enterTemplateNamePlaceholder',
                      )}
                      isError={!!errors.templateName}
                      errorLabel={errors.templateName}
                    />
                  </div>
                  <div>
                    <span className="input-label">
                      {t('internal-inspections:templateView.inspectionName')}
                    </span>
                    <TextField
                      name="name"
                      onChange={handleChange}
                      value={values.name}
                      placeholder={t(
                        'internal-inspections:templateView.enterInspectionNamePlaceholder',
                      )}
                      isError={!!errors.name}
                      errorLabel={errors.name}
                    />
                  </div>
                  <div>
                    <span className="input-label">
                      {t('internal-inspections:templateView.instructionDocuments')}{' '}
                      <span className="input-label--light">
                        {t('internal-inspections:frequencyFields.optional')}
                      </span>
                    </span>
                    <MediaUpload
                      files={values.attachments}
                      mediaTypes={['doc', 'pdf', 'image']}
                      onUpdate={(files) => setFieldValue('attachments', files)}
                      multiple
                    />
                  </div>
                </div>
                <div>
                  <span className="input-label">
                    {t('internal-inspections:templateView.instructions')}{' '}
                    <span className="input-label--light">
                      {t('internal-inspections:frequencyFields.optional')}
                    </span>
                  </span>
                  <EditorText
                    name="description"
                    onChange={(content, text) =>
                      setFieldValue('description', text.trim() ? content : '')
                    }
                    value={values.description}
                    placeholder={t('internal-inspections:templateView.instructionsPlaceholder')}
                  />
                </div>
                <div className="row-settings">
                  <CheckboxField
                    name="noteRequired"
                    data-testid="noteRequired"
                    label={t('internal-inspections:templateView.requiredCompletionNote')}
                    value={values.noteRequired}
                    onChange={handleChange}
                  />
                  <CheckboxField
                    name="photosRequired"
                    data-testid="photosRequired"
                    label={t('internal-inspections:templateView.requiredCompletionPhotos')}
                    value={values.photosRequired}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="ctp-internal-inspections__view__content">
                <AdvancedSetupSection
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>
          </React.Fragment>
        )}
      </Formik>
      <ConfirmPopup
        isOpen={isWarningPopupOpen}
        cancelCallback={() => setIsWarningPopupOpen(false)}
        confirmCallback={onBack}
        cancelText={t('common:actions.back')}
        confirmText={t('internal-inspections:templateView.discardChanges')}
        title={t('internal-inspections:dashboard.cautionTitle')}
        body={t('internal-inspections:templateView.discardWarningBody')}
        className="is-caution"
      />
    </React.Fragment>
  )
}

InspectionTemplateView.propTypes = {
  building: PropTypes.object,
}

export default connect(
  (state) => ({
    building: selectActiveBuilding(state),
  }),
  null,
)(InspectionTemplateView)
